import { z } from 'zod';
import { GAME_HOURS, hoursToMs } from '../universe.js';
import { DEPOSIT_GRADES, STATION_TIERS, MAX_STATION_LEVEL } from '../mining.js';

// ---- Deposits ----

export const DepositTypeSchema = z.enum(['metal', 'energy']);
export type DepositType = z.infer<typeof DepositTypeSchema>;

export const ResourceDepositSchema = z.object({
  id: z.string().uuid(),
  systemId: z.string().uuid(),
  type: DepositTypeSchema,
  capacityPerHour: z.number().int().min(0),
  celestialBodyName: z.string(),
  hasStation: z.boolean(),
  /** Track 7: richness grade revealed by the survey (absent in older payloads = normal). */
  grade: z.enum(DEPOSIT_GRADES).optional(),
  /** Track 7: grade × star-type factor baked into a station's output (1 = unchanged). */
  outputFactor: z.number().optional(),
  /** Track 7: the star-type part of the factor, for the breakdown in the system panel. */
  starFactor: z.number().optional(),
  /** Track 7: what a level-1 station would deliver per hour from this deposit (before tech, tier and transport). */
  yieldPerHour: z.number().int().optional(),
});
export type ResourceDeposit = z.infer<typeof ResourceDepositSchema>;

// ---- Constructions ----

export const ConstructionTypeSchema = z.enum(['outpost', 'mining_station', 'energy_station', 'logistics_hub']);
export type ConstructionType = z.infer<typeof ConstructionTypeSchema>;

export const ConstructionStatusSchema = z.enum(['in_progress', 'completed', 'failed']);
export type ConstructionStatus = z.infer<typeof ConstructionStatusSchema>;

export const ConstructionClientSchema = z.object({
  id: z.string().uuid(),
  type: ConstructionTypeSchema,
  status: ConstructionStatusSchema,
  systemId: z.string().uuid(),
  depositId: z.string().uuid().nullable(),
  startedAt: z.string().datetime(),
  completesAt: z.string().datetime().nullable(),
});
export type ConstructionClient = z.infer<typeof ConstructionClientSchema>;

// ---- Stations ----

export const StationSchema = z.object({
  id: z.string().uuid(),
  type: ConstructionTypeSchema,
  depositId: z.string().uuid().nullable(),
  productionRatePerHour: z.number().int().min(0),
  status: z.enum(['active', 'damaged', 'decommissioned']),
});
export type Station = z.infer<typeof StationSchema>;

// ---- Resources ----

/** Net rates: metal after deficit efficiency, energy after upkeep (may be negative). */
export const CivilizationResourcesSchema = z.object({
  metal: z.number().int().min(0),
  energy: z.number().int().min(0),
  credits: z.number().int(),
  metalPerHour: z.number().int(),
  energyPerHour: z.number().int(),
  /** Mining efficiency right now (1 = full; below 1 while energy is exhausted). */
  efficiency: z.number().optional(),
  energyUpkeepPerHour: z.number().int().optional(),
  /** Metal maintenance (stations, outposts, fleets…) already included in `metalPerHour`. */
  metalUpkeepPerHour: z.number().int().optional(),
  /** Storage capacity: production stops here (an already larger stock is kept). */
  metalCapacity: z.number().int().optional(),
  energyCapacity: z.number().int().optional(),
  /**
   * The server's clock when this snapshot was settled. The client interpolates `amount + rate × elapsed`
   * from this instant (never from its own clock alone) and ignores snapshots older than the one it holds.
   */
  serverTime: z.string().datetime().optional(),
  /** Research points (RP) balance and income per hour; absent when the civilization has no research state yet. */
  rp: z.number().int().optional(),
  rpPerHour: z.number().optional(),
});
export type CivilizationResources = z.infer<typeof CivilizationResourcesSchema>;

// ---- System ownership ----

export const SystemOwnerSchema = z.object({
  civilizationId: z.string().uuid(),
  civilizationName: z.string(),
  civilizationColor: z.string(),
  acquiredAt: z.string().datetime(),
});
export type SystemOwner = z.infer<typeof SystemOwnerSchema>;

// ---- API commands ----

export const BuildOutpostCommandSchema = z.object({
  systemId: z.string().uuid(),
  fleetId: z.string().uuid(),
  idempotencyKey: z.string().min(1).max(128),
});
export type BuildOutpostCommand = z.infer<typeof BuildOutpostCommandSchema>;

export const BuildStationCommandSchema = z.object({
  systemId: z.string().uuid(),
  /** Required for mining/energy stations; a logistics hub is built on the system, not on a deposit. */
  depositId: z.string().uuid().optional(),
  fleetId: z.string().uuid(),
  type: z.enum(['mining_station', 'energy_station', 'logistics_hub']),
  idempotencyKey: z.string().min(1).max(128),
}).refine(c => c.type === 'logistics_hub' || !!c.depositId, { message: 'depositId required', path: ['depositId'] });
export type BuildStationCommand = z.infer<typeof BuildStationCommandSchema>;

// ---- Ship Orders ----

export const ShipOrderClientSchema = z.object({
  id: z.string().uuid(),
  systemId: z.string().uuid(),
  shipClass: z.enum(['corvette', 'frigate', 'cruiser', 'construction', 'scout']),
  shipName: z.string(),
  metalCost: z.number().int(),
  energyCost: z.number().int(),
  status: z.string(),
  startedAt: z.string().datetime(),
  completesAt: z.string().datetime().nullable(),
  /** Fleet the ship will join; null → the system's local reserve fleet. */
  targetFleetId: z.string().uuid().nullable(),
  targetFleetName: z.string().nullable(),
  /** M13: felszerelés */
  weapon: z.enum(['kinetic', 'beam', 'missile']).optional(),
  module: z.enum(['none', 'plating', 'shield', 'pointdef', 'ecm']).optional(),
});
export type ShipOrderClient = z.infer<typeof ShipOrderClientSchema>;

export const BuildShipCommandSchema = z.object({
  shipClass: z.enum(['corvette', 'frigate', 'cruiser', 'construction', 'scout']),
  targetFleetId: z.string().uuid().optional(),
  weapon: z.enum(['kinetic', 'beam', 'missile']).optional(),
  module: z.enum(['none', 'plating', 'shield', 'pointdef', 'ecm']).optional(),
  idempotencyKey: z.string().min(1).max(128),
});
export type BuildShipCommand = z.infer<typeof BuildShipCommandSchema>;

// Ship costs (also exported for frontend use)
export const SHIP_BUILD_COSTS: Record<'corvette' | 'frigate' | 'cruiser', { metal: number; energy: number; timeMs: number }> = {
  corvette: { metal: 200, energy: 50,  timeMs: hoursToMs(GAME_HOURS.corvette) },
  frigate:  { metal: 400, energy: 100, timeMs: hoursToMs(GAME_HOURS.frigate) },
  cruiser:  { metal: 800, energy: 200, timeMs: hoursToMs(GAME_HOURS.cruiser) },
};

// ---- Economy balance (M6/9–M6/10) — shared so the UI shows the server's numbers ----

export const ECONOMY_BALANCE = {
  /** Baseline output of the home system's own planets. */
  homeMetalPerHour: 240,
  homeEnergyPerHour: 240,
  /**
   * M10.10: share of a deposit's capacity a station actually delivers. The stored deposit capacity is
   * unchanged (no data migration); one L1 mine now yields about the home system's own output instead of 2.5x.
   */
  depositYield: 0.3,
  maxStationLevel: MAX_STATION_LEVEL,
  /** Output multiplier by station level (index = level - 1). Derived from `STATION_TIERS` (Track 7). */
  stationLevelMultiplier: STATION_TIERS.map(t => t.output) as readonly number[],
  stationUpgradeCost: Object.fromEntries(STATION_TIERS.filter(t => t.upgradeCost).map(t => [t.level, t.upgradeCost!])) as Record<number, { metal: number; energy: number }>,
  /** Energy consumed per hour. */
  upkeepPerHour: {
    miningStationPerLevel: 60,
    outpost: 30,
    corvette: 12,
    frigate: 24,
    cruiser: 48,
  },
  /**
   * M10.10: metal maintenance per hour (infrastructure and fleet upkeep). Paid continuously out of the
   * income; the stockpile never goes below zero, so an over-extended empire simply stops earning.
   */
  metalUpkeepPerHour: {
    stationPerLevel: 10,
    outpost: 12,
    corvette: 5,
    frigate: 10,
    cruiser: 20,
  },
  /** M10.10: administrative sprawl: outposts beyond `freeOutposts` cost this much more each (per extra outpost). */
  sprawl: { freeOutposts: 4, perOutpost: 0.06 },
  /** M10.10: a new outpost costs base x growth^(owned outposts - free), so late expansion is a real investment. */
  outpostCost: { freeOutposts: 3, growth: 1.16 },
  /** M10.10: stockpile capacity; production above it is lost (stored goods are only worth what can be kept). */
  storage: { metalBase: 12000, metalPerSystem: 3000, energyBase: 12000, energyPerSystem: 1500 },
  /** Mines never drop below this efficiency during an energy shortage. */
  minDeficitEfficiency: 0.25,
} as const;

/** Upkeep multiplier of outposts for an empire with `outposts` outposts (excluding the home system). */
export function sprawlMultiplier(outposts: number): number {
  const b = ECONOMY_BALANCE.sprawl;
  return 1 + b.perOutpost * Math.max(0, outposts - b.freeOutposts);
}

/** Metal price of the next outpost for an empire that already owns `outposts` outposts (rounded to 5). */
export function outpostMetalCost(baseMetal: number, outposts: number): number {
  const b = ECONOMY_BALANCE.outpostCost;
  return Math.round(baseMetal * b.growth ** Math.max(0, outposts - b.freeOutposts) / 5) * 5;
}

/**
 * Stockpile capacity for an empire owning `systems` systems (home included). Research v2: the technology bonus
 * (`techModifiers().storage`) and the megaproject bonus multiply each other: base × (1 + tech) × (1 + mega).
 */
export function storageCapacity(systems: number, techBonus = 0, megaBonus = 0): { metal: number; energy: number } {
  const b = ECONOMY_BALANCE.storage;
  const n = Math.max(0, systems - 1);
  const mult = (1 + Math.max(0, techBonus)) * (1 + Math.max(0, megaBonus));
  return { metal: Math.round((b.metalBase + b.metalPerSystem * n) * mult), energy: Math.round((b.energyBase + b.energyPerSystem * n) * mult) };
}

/** Hours of a stock's net production at which the stockpile is full (null: not filling / already full). */
export function hoursUntilFull(stock: number, cap: number, netPerHour: number): number | null {
  if (netPerHour <= 0 || stock >= cap) return null;
  return (cap - stock) / netPerHour;
}

export const EconomySourceSchema = z.object({
  id: z.string(),
  kind: z.enum(['home', 'mining_station', 'energy_station', 'trade', 'logistics_hub']),
  resource: z.enum(['metal', 'energy', 'both']),
  systemId: z.string().uuid(),
  systemName: z.string(),
  depositName: z.string().nullable(),
  level: z.number().int(),
  status: z.string(),
  basePerHour: z.number().int(),
  /** Output after level multiplier and current efficiency (per resource for 'both'). */
  effectivePerHour: z.number().int(),
  upkeepPerHour: z.number().int(),
  upgrade: z.object({ toLevel: z.number().int(), metal: z.number().int(), energy: z.number().int(), timeMs: z.number().int() }).nullable(),
  upgradingTo: z.number().int().nullable(),
  upgradeCompletesAt: z.string().datetime().nullable(),
  /** Track 7: tier name, deposit grade and the logistics factor that explains `effectivePerHour`. */
  tierName: z.string().optional(),
  grade: z.enum(DEPOSIT_GRADES).optional(),
  starType: z.string().optional(),
  outputFactor: z.number().optional(),
  transport: z.object({
    efficiency: z.number(),
    distanceLy: z.number(),
    hubSystemId: z.string().uuid().nullable(),
    hubSystemName: z.string().nullable(),
    hubKind: z.enum(['capital', 'hub', 'trade']).nullable(),
    /** Output lost to the distance, per hour (rounded). */
    lostPerHour: z.number().int(),
  }).optional(),
});
export type EconomySource = z.infer<typeof EconomySourceSchema>;

export const EconomyOverviewSchema = z.object({
  resources: z.object({ metal: z.number().int(), energy: z.number().int(), credits: z.number().int() }),
  /** `upkeepPerHour` (M10.10): metal maintenance; `netPerHour` is the metal income after efficiency and maintenance. */
  metal: z.object({ grossPerHour: z.number().int(), netPerHour: z.number().int(), upkeepPerHour: z.number().int().optional() }),
  energy: z.object({ grossPerHour: z.number().int(), upkeepPerHour: z.number().int(), netPerHour: z.number().int() }),
  upkeep: z.object({ stations: z.number().int(), outposts: z.number().int(), fleets: z.number().int() }),
  /** M10.10: metal maintenance by category (absent in older payloads). */
  metalUpkeep: z.object({ stations: z.number().int(), outposts: z.number().int(), fleets: z.number().int(), admin: z.number().int().optional(), projects: z.number().int().optional() }).optional(),
  /** M13: közigazgatási energiateher és kereskedelmi útvonalak. */
  adminEnergy: z.number().int().optional(),
  routes: z.object({ count: z.number().int(), blockaded: z.number().int() }).optional(),
  /** Track 7: logistics — hubs, builder limit and the output lost to distance. */
  logistics: z.object({
    hubs: z.array(z.object({ systemId: z.string().uuid(), systemName: z.string(), kind: z.enum(['capital', 'hub', 'trade']) })),
    hubCount: z.number().int(),
    maxHubs: z.number().int(),
    averageEfficiency: z.number(),
    lostMetalPerHour: z.number().int(),
    lostEnergyPerHour: z.number().int(),
    builders: z.number().int(),
    maxBuilders: z.number().int(),
    hubCost: z.object({ metal: z.number().int(), energy: z.number().int(), timeMs: z.number().int() }),
  }).optional(),
  /** M10.10: stockpile capacity; production above it is lost. */
  storage: z.object({ metal: z.number().int(), energy: z.number().int() }).optional(),
  /** M10.10: when the metal stockpile runs dry at the current (negative) net rate. */
  metalEmptyAt: z.string().datetime().nullable().optional(),
  efficiency: z.number(),
  /** When stored energy runs out at the current net rate (null if not draining). */
  energyEmptyAt: z.string().datetime().nullable(),
  sources: z.array(EconomySourceSchema),
  ledger: z.array(z.object({ metal: z.number().int(), energy: z.number().int(), reason: z.string(), createdAt: z.string().datetime() })),
  sinceLastVisit: z.object({
    since: z.string().datetime(),
    produced: z.object({ metal: z.number().int(), energy: z.number().int() }),
    spent: z.object({ metal: z.number().int(), energy: z.number().int() }),
  }).nullable(),
});
export type EconomyOverview = z.infer<typeof EconomyOverviewSchema>;

export const UpgradeStationCommandSchema = z.object({ idempotencyKey: z.string().min(1).max(128) });
export type UpgradeStationCommand = z.infer<typeof UpgradeStationCommandSchema>;
